import { useEffect, useState } from "react"
import { notification, Tooltip, UploadFile } from "antd"
import { VehicleType } from "@/types/vehicle.types"
import { formatPhoneNumber } from "@/utils/FormatPhoneNumber"
import { getColumnDescription } from "@/utils/excel"
import { read, utils } from "xlsx"
import { useRecoilValue } from "recoil"
import { useTranslation } from "react-i18next"
import { localizationAtom } from "@/atoms/localizationAtom"
import { useTeams } from "@/hooks/useTeams"
import { useUser } from "@/hooks/useUser"
import { Driver } from "@/types/drivers.types"
import { ISheetRow } from "./upload.types"
import { v0Properties } from "./versions/version_0"
import { useDrivers } from "@/hooks/useDrivers"
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React from "react"

interface IUseUploadTeamplate {
  file: UploadFile[]
  setFile: any
  processTemplateFile: (uploadCallback: () => Promise<any>) => Promise<void>
  progressPercent: number
  isLoading: boolean
  isDisabled: boolean
}
export type AddressDetails = {
  postalCode: string
  lat: number
  lng: number
  isWarning?: boolean
}

const formatVehicle = (vehicle: string): VehicleType => {
  const spanishedVehicle = {
    BICI: VehicleType.BIKE,
    MOTO: VehicleType.MOTORCYCLE,
    COCHE: VehicleType.CAR,
    FURGONETA: VehicleType.VAN,
    CAMIÓN: VehicleType.LORRY,
  }[vehicle.toUpperCase()]
  if (vehicle.toUpperCase() === "TRUCK") {
    return VehicleType.LORRY
  }
  return (
    spanishedVehicle ||
    VehicleType[vehicle.toUpperCase() as keyof typeof VehicleType] ||
    VehicleType.MOTORCYCLE
  )
}

const getErrorText = (error: string | undefined) => {
  switch (error) {
    case "invalid_phone":
      return "errors.invalid_phone_number"
    default:
      return "common.other"
  }
}

export const useUploadTemplate = (): IUseUploadTeamplate => {
  const { t } = useTranslation()

  const { user } = useUser()

  const { locale } = useRecoilValue(localizationAtom)

  const [file, setFile] = useState<UploadFile[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [progressPercent, setProgressPercent] = useState<number>(0)

  const referenceCountry = user?.org?.country || locale

  const MAX_UPLOAD_LIMIT = 1000

  const { createDriver } = useDrivers()
  const { findTeamByName } = useTeams()

  useEffect(() => {
    setIsDisabled(!file)
  }, [file])

  useEffect(() => {
    if (file) {
      setIsDisabled(isLoading)
    }
  }, [isLoading])

  const convertSheetDataToDriverPayload = async (driverRow: ISheetRow) => {
    try {
      let {
        name,
        email,
        phone,
        teams,
        "length_capacity(cm)": lengthCapacity,
        "height_capacity(cm)": heightCapacity,
        "width_capacity(cm)": widthCapacity,
        "weight_capacity(kg)": weightCapacity,
        vehicle,
        maxStopsPerRoute,
        "maxRouteDistance(km)": maxRouteDistance,
        maxRouteDuration,
      } = driverRow
      const driver: Driver = {
        id: "",
        name: name || "",
        email: email || "",
        phone: phone ? formatPhoneNumber(String(phone), referenceCountry) : "",
        vehicleType: formatVehicle(vehicle) || VehicleType.MOTORCYCLE,
        lengthCapacity: lengthCapacity ? Number(lengthCapacity) : undefined,
        heightCapacity: heightCapacity ? Number(heightCapacity) : undefined,
        widthCapacity: widthCapacity ? Number(widthCapacity) : undefined,
        weightCapacity: weightCapacity ? Number(weightCapacity) : undefined,
        status: "offline",
        config: {
          maxBatchSize: maxStopsPerRoute ? Number(maxStopsPerRoute) : undefined,
          maxDistanceInMeters: maxRouteDistance ? Number(maxRouteDistance) * 1000 : undefined,
        },
      }

      // Teams
      if (teams) {
        const teamNames = teams.split(",").map((team) => team.trim())
        const teamIds = teamNames
          .map((teamName) => {
            const team = findTeamByName(typeof teamName === "number" ? `${teamName}` : teamName)
            if (team) {
              return team.id
            }
            return undefined
          })
          .filter((teamId) => teamId !== undefined) as number[]
        driver.teamIDs = teamIds
      }

      // Parse route duration
      if (maxRouteDuration) {
        const [hours, minutes] = maxRouteDuration.split(":")
        if (!driver.config) {
          driver.config = {}
        }
        driver.config.maxShiftTimeInMinutes = Number(hours) * 60 + Number(minutes)
      }

      return driver
    } catch (err: any) {
      throw new Error(t("errors.draftUpload.convertTemplateError"), err)
      // handleFileError(t("errors.draftUpload.convertTemplateError"), err)
      // return
    }
  }
  const convertFiletoJson: () => Promise<any[] | undefined> = async () => {
    try {
      if (!file.length || !file?.[0]) {
        throw new Error("No file selected")
      }

      const data = await file[0].originFileObj?.arrayBuffer()
      const workbook = read(data, {
        cellStyles: true,
      })
      // Take sheet 0 or sheet with name "Kosmo Template"
      const sheet = workbook.Sheets[workbook.SheetNames[0] || "Kosmo Template"]
      if (!sheet) {
        throw new Error(t("errors.draftUpload.noTemplateFound"))
      }
      //format sheet to json
      var range = utils.decode_range(sheet!["!ref"]!)

      range.s.r = 1 // <-- zero-indexed, so setting to 2 will skip row 0,1

      sheet!["!ref"] = utils.encode_range(range)

      const currentVersion = {
        properties: v0Properties,
        headerRowIndex: 1,
        origin: "A1",
        minColumnsCount: 8,
      }
      const versionProperties = currentVersion.properties

      // count number of columns with data
      // we need to check A2, B2, C2 etc. because the first row is the header
      // iterate by letters A, B, C, D etc.
      let columnCount = 0
      for (let i = 0; i < versionProperties.length; i++) {
        const letter = getColumnDescription(i)
        const cell = sheet[letter + currentVersion.headerRowIndex]
        if (cell && cell.v) {
          columnCount++
        } else {
          break
        }
      }

      if (columnCount < currentVersion.minColumnsCount) {
        // To translate
        throw new Error(t("errors.draftUpload.templateErrorVersion"))
      }

      utils.sheet_add_aoa(sheet!, [versionProperties], { origin: currentVersion.origin })

      const table: any[] = utils
        .sheet_to_json(sheet!, { raw: true })
        .filter((item: any) => item.name || item.phone)

      if (table.length > MAX_UPLOAD_LIMIT) {
        throw new Error(t("errors.driverUpload.limitReached", { driverLimit: MAX_UPLOAD_LIMIT }))
      }

      return table
    } catch (err: any) {
      throw err
    }
  }

  const processTemplateFile = async (uploadCallback: () => Promise<any>) => {
    const errors = [] as { index: number; reason: string }[]
    try {
      setIsLoading(true)
      setProgressPercent(0)
      const parsedRows: ISheetRow[] | undefined = await convertFiletoJson()
      const submittedDrivers = parsedRows?.filter(
        (row, index) => Object.keys(row)?.length >= 2 && row?.__rowNum__ - index === 1
      )

      if (!submittedDrivers) {
        setIsLoading(false)
        return
      }
      if (submittedDrivers?.length === 0) {
        throw new Error(t("errors.driverUpload.noDriversFound"))
      }
      // We use a tuple with index to preserve original order
      const requestDrivers = [] as { driver: Driver; index: number }[]
      let createdDrivers = 0
      let requestDriver: Driver | undefined
      const processDriver = async (driverRow: ISheetRow, index: number) => {
        try {
          requestDriver = await convertSheetDataToDriverPayload(driverRow)
        } catch (err: any) {
          // We add 2 because the first row is the header
          errors.push({ index: index + 2, reason: err?.message || "" })
        }

        if (!requestDriver) {
          setIsLoading(false)
          return
        }

        requestDrivers.push({ driver: requestDriver, index })
        // We set the progress percent as the number of drivers processed by half (meaning 50% if all rows are processed)
        // We set the progress percent as the number of drivers created by half (meaning 50% if all drivers are created)
        // Total progress is 100% once all drivers are created
        setProgressPercent(
          Math.round(
            (requestDrivers.length / submittedDrivers.length) * 50 +
              (createdDrivers / submittedDrivers.length) * 50
          )
        )
        try {
          await createDriver(requestDriver)
        } catch (err: any) {
          errors.push({
            index,
            reason: err?.response?.data?.details === "invalid phone" ? "invalid_phone" : "",
          })
        }
        createdDrivers++

        setProgressPercent(
          Math.round(
            (requestDrivers.length / submittedDrivers.length) * 50 +
              (createdDrivers / submittedDrivers.length) * 50
          )
        )
      }

      const batchSize = Math.min(submittedDrivers.length, 45)
      for (let i = 0; i < submittedDrivers.length; i += batchSize) {
        await Promise.all(
          submittedDrivers
            .slice(i, i + batchSize)
            .map((order, index) => processDriver(order, i + index))
        )
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      if (uploadCallback) {
        await uploadCallback()
      }

      if (errors.length > 0) {
        const errorsToShow = errors.slice(0, 50)
        setTimeout(
          () =>
            notification.open({
              message: t("errors.draftUpload.someDriversCouldNotBeUploaded"),
              description: (
                <div className="flex flex-col">
                  <p>{t("errors.draftUpload.followingRowsCouldNotBeUploaded")}</p>
                  <div className="flex flex-wrap">
                    {errorsToShow
                      ?.sort((a, b) => a.index - b.index)
                      ?.map((error, index) => (
                        <>
                          <Tooltip
                            title={getErrorText(error?.reason)}
                            key={error?.index}
                            overlayStyle={{ zIndex: 3001 }}
                          >
                            <p className="cursor-pointer" key={error?.index}>
                              {error?.index}
                            </p>
                            {index < errorsToShow.length - 1 && ", "}
                          </Tooltip>
                        </>
                      ))}
                  </div>
                  {errors?.length > 30 && (
                    <p className="font-semibold">
                      {t("errors.draftUpload.andMore", { count: errors?.length - 50 })}
                    </p>
                  )}
                </div>
              ),
              duration: null,
              icon: (
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  style={{ color: "#A91B0D", height: "1.2em" }}
                />
              ),
            }),
          500
        )
      }

      setFile([])
      setIsLoading(false)
    } catch (err: any) {
      setIsLoading(false)
      setFile([])
      throw err
    }
  }

  return {
    file,
    processTemplateFile,
    isLoading,
    progressPercent,
    setFile,
    isDisabled,
  }
}
